import {
  SaleDepositAdditionalExpense,
  SaleDepositResponse,
  SaleDepositSlip,
} from "../../types/sale-deposit.type";
import { numberWithCommas } from "../../utils";

export interface TransferSlipSource {
  slips?: SaleDepositSlip[] | null;
  transfer_amount?: number | null;
  transfer_date?: string | null;
  transfer_time?: string | null;
  slip_url?: string | null;
}

/** Numeric zero is initialized; only null/undefined means BO has not notified. */
export const hasRequiredTransferAmount = (
  value: number | null | undefined,
): value is number => value != null;

/**
 * Prefer the normalized multi-slip contract. During a rolling deployment,
 * synthesize one metadata row from the legacy first-slip fields when needed.
 */
export const normalizeTransferSlips = (
  source: TransferSlipSource,
): SaleDepositSlip[] => {
  if (source.slips && source.slips.length > 0) {
    return [...source.slips].sort((a, b) => a.sequence - b.sequence);
  }

  if (!source.slip_url) return [];

  return [
    {
      uuid: "legacy-slip",
      sequence: 1,
      transfer_amount: source.transfer_amount ?? null,
      transfer_date: source.transfer_date ?? null,
      transfer_time: source.transfer_time ?? null,
      slip_url: source.slip_url,
    },
  ];
};

/**
 * Both operands arrive already rounded to 2dp from the server, but subtracting them in JS
 * reintroduces binary-float noise (0.1 - 0.3 + 0.2 === 5.55e-17). Round once here so callers
 * can compare against 0 to pick a colour without a false "off by a rounding error".
 */
export const roundMoney = (value: number): number =>
  Math.round((value + Number.EPSILON) * 100) / 100;

/**
 * ผลต่าง = ยอดโอน − ยอดต้องโอน (actual − expected).
 * Negative means the route transferred LESS than it owed, which is the case finance chases.
 * Null when no slip has been submitted yet — treating the missing transfer as 0 would paint
 * every un-submitted route with a large fake shortfall.
 */
export const transferDiff = (
  required: number | null | undefined,
  transfer: number | null | undefined,
): number | null => {
  if (transfer == null || required == null) return null;
  return roundMoney(Number(transfer) - Number(required));
};

/** The one label for cash in hand. Both screens read it from here so they cannot drift apart. */
export const CASH_IN_HAND_LABEL = "เงินสด (ในมือ)";

/** Why the column is a whole number, and the sum the reader can do in their head. */
export const CASH_IN_HAND_HINT =
  "เงินสดที่พนักงานถืออยู่จริง ตัดเศษสตางค์รายใบ (ไม่รวมเงินโอน/QR) · เงินสด(ในมือ) + ฝากเก็บ − ค่าใช้จ่าย = ยอดที่ต้องโอน";

/**
 * What a ที่ต้องโอน says beside itself once the route-day has cash ใบลดหนี้ / ใบเพิ่มหนี้: the
 * figure already has them in. The verb follows the way the notes moved it.
 */
export const NET_OF_NOTES_HINT = {
  deducted: "หักใบลดหนี้แล้ว",
  added: "บวกใบเพิ่มหนี้แล้ว",
} as const;

/**
 * เงินสด (ในมือ) — the WHOLE-BAHT cash the salesperson is carrying.
 *
 * The backend counts it bill by bill (ตัดเศษรายใบ) and sends `physical_cash_total_value`;
 * prefer that. A stored deposit row keeps only the two totals, so it falls back to
 * รวมเงินสด − เงินโอน — exact, because the backend assembles รวมเงินสด as เงินสด + เงินโอน.
 * Clamped at 0, and floored: a legacy row written before ตัดเศษ can still carry satang, and
 * showing them would contradict the whole-baht figure the remit is built on.
 */
export const physicalCash = (row: {
  cash_total_value: number;
  transfer_total_value?: number | null;
  physical_cash_total_value?: number | null;
}): number => {
  const counted =
    row.physical_cash_total_value ??
    roundMoney(row.cash_total_value - (row.transfer_total_value ?? 0));
  return Math.max(0, Math.floor(counted));
};

/**
 * Cash in hand stays a whole-baht FIGURE — the satang are already cut per bill upstream — but
 * it is printed to two decimals so the column lines up with every other money column in the
 * report. The trailing ".00" is therefore always literal, never a rounded-away satang.
 */
export const formatCashInHand = (value: number): string =>
  new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

/**
 * Names the routes that have not sent a slip yet, for the ที่ต้องโอน/ยอดโอน cell they are
 * missing from. Every aggregate — the two totals rows and a branch-day row — counts an
 * unsent slip as 0, so without this the figure reads as money that went missing rather than
 * money not yet sent. Empty when there is nothing outstanding, which renders no tooltip.
 */
export const pendingSlipHint = (pendingCount: number): string =>
  pendingCount > 0 ? `ยังไม่ส่งสลิป ${numberWithCommas(pendingCount)} สาย` : "";

export interface ExpenseLine {
  label: string;
  amount: number;
}

/** The five fixed expense buckets a deposit always carries, each NOT NULL DEFAULT 0. */
export type FixedExpenseField = keyof Pick<
  SaleDepositResponse,
  | "fuel_expense_amount"
  | "transfer_fee_amount"
  | "toll_fee_amount"
  | "parking_fee_amount"
  | "other_expense_amount"
>;

/**
 * The five fixed buckets and their Thai labels, in the order the BO form presents them.
 * Read by everything that names an expense — the read-only breakdown, the แจ้งยอดโอน form
 * and the back-office correction — so a label change cannot land on one screen only.
 */
export const FIXED_EXPENSE_FIELDS: Array<{
  key: FixedExpenseField;
  label: string;
}> = [
  { key: "fuel_expense_amount", label: "ค่าน้ำมัน (เงินสด)" },
  { key: "transfer_fee_amount", label: "ค่าธรรมเนียมการโอน" },
  { key: "toll_fee_amount", label: "ค่าทางด่วน" },
  { key: "parking_fee_amount", label: "ค่าจอดรถ" },
  { key: "other_expense_amount", label: "ค่าใช้จ่ายอื่น" },
];

/**
 * Flattens a deposit's expenses into display lines: the five fixed buckets followed by the
 * free-form `additional_expenses` rows. Every bucket is NOT NULL DEFAULT 0 server-side, so
 * zero-value buckets are kept — hiding them would leave the user unable to tell "นี่คือ 0"
 * from "ยังไม่ได้กรอก".
 */
export const buildExpenseLines = (row: {
  fuel_expense_amount: number;
  transfer_fee_amount: number;
  toll_fee_amount: number;
  parking_fee_amount: number;
  other_expense_amount: number;
  additional_expenses?: SaleDepositAdditionalExpense[] | null;
}): ExpenseLine[] => [
  ...FIXED_EXPENSE_FIELDS.map(({ key, label }) => ({
    label,
    amount: Number(row[key] ?? 0),
  })),
  ...(row.additional_expenses ?? []).map((expense) => ({
    label: expense.description,
    amount: Number(expense.amount ?? 0),
  })),
];

// ---- Money entry ----
// Shared by every screen that types an expense figure — แจ้งยอดโอน, the slip-amount
// correction and the back-office expense edit — so all three accept and reject the same
// keystrokes. Amounts are typed as strings while editing: a half-typed "12." is a valid
// intermediate state that a number-typed input cannot hold.

/** Accepts only a non-negative decimal with up to 2 fraction digits (or empty while typing). */
export const isMoneyInput = (raw: string): boolean =>
  raw === "" || /^\d*\.?\d{0,2}$/.test(raw);

/**
 * Every money field is optional: a blank input counts as 0, not a missing value.
 * A non-empty value must still parse to a finite, non-negative number — `null` says it
 * does not, so the caller can refuse the save and say which field is wrong.
 */
export const parseNonNegativeMoney = (raw: string): number | null => {
  if (!raw.trim()) return 0;
  const amount = Number(raw);
  return Number.isFinite(amount) && amount >= 0 ? amount : null;
};

/**
 * A stored 0 is rendered blank: blank already means 0 on save, and showing "0" would force
 * the user to clear the field before typing.
 */
export const toDraftAmount = (value: number | null | undefined): string =>
  typeof value === "number" && Number.isFinite(value) && value !== 0
    ? String(value)
    : "";
